<template>
    <div
        class="panel"
        :class="[`panel-${getColor(status)}`]"
        style="display: flex; flex-direction: column; flex-grow: 1"
    >
        <div class="panel-heading text-center" style="height: 130px; display: flex; align-items: center; justify-content: center; position: relative">
            <i
                class="glyphicon"
                :class="[`glyphicon-${getIcon(status)}`]"
                style="font-size: 100px; line-height: 1"
            ></i>
            <time :datetime="updatedAt.toISOString()" style="position: absolute; top: 4px; right: 6px; font-size: 11px; opacity: 0.5">{{ updatedAt.toLocaleTimeString() }}</time>
        </div>
        <div style="display: flex; justify-content: space-between; min-height: 36px">
            <div
                v-for="stage in stages"
                :key="stage"
                :class="`bg-${getColor(stagesStatus[stage])}`"
                style="flex-grow: 1; text-align: center; border-bottom: 1px solid #f5f5f5"
            >
                <i class="glyphicon" :class="[`glyphicon-${getIcon(stagesStatus[stage])}`, `text-${getColor(stagesStatus[stage])}`]"></i>
                <div>{{ stage.replace('stage', '') }}</div>
            </div>
        </div>
        <div class="panel-body text-center" style="flex-grow: 1">
            <h2 style="margin-top: 0">
                {{ caption }}
            </h2>
            <ul class="list-unstyled" style="font-size: 16px; margin-top: 8px">
                <li v-if="meta.numberOfMails !== undefined">
                    {{ meta.numberOfMails }} Mails waiting
                </li>
                <li v-if="meta.oldestWaitingMinutes > 0">
                    oldest waiting for {{ meta.oldestWaitingMinutes }} min
                </li>
                <li v-if="meta.packages && meta.packages.length">
                    {{ meta.packages.join(', ') }}
                </li>
                <li v-if="meta.failed !== undefined || meta.delayed !== undefined || meta.longRunning !== undefined" style="display: flex; gap: 1rem; list-style: none; justify-content: center">
                    <span v-if="meta.failed !== undefined">Failed: {{ meta.failed }}</span>
                    <span v-if="meta.delayed !== undefined">Delayed: {{ meta.delayed }}</span>
                    <span v-if="meta.longRunning !== undefined">Long running: {{ meta.longRunning }}</span>
                </li>
                <li v-if="meta.sites && meta.sites.length" style="list-style: none; padding: 0">
                    <div style="display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 8px">
                        <div
                            v-for="site in meta.sites"
                            :key="site.name"
                            :style="`background: ${getSiteBgColor(site)}; border-radius: 4px; padding: 6px 10px; min-width: 110px`"
                        >
                            <div style="font-weight: bold; margin-bottom: 4px; font-size: 14px">
                                {{ site.name }}
                            </div>
                            <div v-for="domain in site.domains" :key="domain.label" style="font-size: 12px; white-space: nowrap">
                                <i class="glyphicon" :class="getDomainIcon(domain)" style="vertical-align: middle"></i>
                                {{ domain.label }}
                                <template v-if="domainHasUpdates(domain)">
                                    <span v-if="domain.core" style="color: #999"> Core→{{ domain.core }}</span>
                                    <span v-if="domain.plugins > 0" style="color: #999"> P:{{ domain.plugins }}</span>
                                    <span v-if="domain.themes > 0" style="color: #999"> T:{{ domain.themes }}</span>
                                </template>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    props: {
        path: {
            type: String,
            required: true,
        },
        caption: {
            type: String,
            required: true,
        },
        timeout: {
            type: Number,
            required: true,
        },
        password: {
            type: String,
            required: true,
        },
        stages: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            status: 'unknown',
            meta: {},
            updatedAt: new Date(),
            stagesStatus: {},
        };
    },
    created() {
        this.stages.forEach((stage) => {
            this.stagesStatus[stage] = 'unknown';
        });
    },
    mounted() {
        this.update('prod');
        this.stages.forEach((stage) => this.update(stage));
    },
    methods: {
        getColor(status) {
            switch (status) {
                case 'ok':
                    return 'success';

                case 'unknown':
                    return 'warning';

                case 'alert':
                    return 'danger';
            }

            throw new Error(`unknown status: "${status}"`);
        },
        getSiteBgColor(site) {
            const domains = site.domains || [];
            if (domains.some((d) => !d.reachable || d.core)) {
                return '#f2dede';
            }
            if (domains.some((d) => d.api_error || d.plugins > 0 || d.themes > 0)) {
                return '#fcf8e3';
            }
            return '#dff0d8';
        },
        domainHasUpdates(domain) {
            return domain.core || domain.plugins > 0 || domain.themes > 0;
        },
        getDomainIcon(domain) {
            if (!domain.reachable) {
                return 'glyphicon-remove text-danger';
            }
            if (domain.core) {
                return 'glyphicon-remove text-danger';
            }
            if (domain.api_error) {
                return 'glyphicon-question-sign text-warning';
            }
            if (domain.plugins > 0 || domain.themes > 0) {
                return 'glyphicon-alert text-warning';
            }
            return 'glyphicon-ok text-success';
        },
        getIcon(status) {
            switch (status) {
                case 'ok':
                    return 'ok';

                case 'unknown':
                    return 'alert';

                case 'alert':
                    return 'remove';
            }

            throw new Error(`unknown status: "${status}"`);
        },
        update(environment) {
            const baseUrl = environment === 'prod' ? window.location.origin : `https://${environment}.kigaroo.com`;
            axios
                .get(`${baseUrl}/admin/${this.path}`, { auth: { username: 'dashboard', password: this.password } })
                .then((response) => {
                    if (environment === 'prod') {
                        this.status = response.data.status;
                        this.meta = response.data.meta || {};
                        this.updatedAt = new Date();
                    } else {
                        this.stagesStatus[environment] = response.data.status;
                    }
                    setTimeout(() => this.update(environment), this.timeout * 1000);
                })
                .catch(() => {
                    if (environment === 'prod') {
                        this.status = 'unknown';
                        this.meta = {};
                        this.updatedAt = new Date();
                    } else {
                        this.stagesStatus[environment] = 'unknown';
                    }
                    setTimeout(() => this.update(environment), this.timeout * 1000);
                });
        },
    },
};
</script>
